import { Bubble } from '@ant-design/x';
import { XMarkdown } from '@ant-design/x-markdown';
import { Skeleton } from 'antd';
import { BalanceAIColoredIcon, MicNoneIcon } from 'common/icons';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatContext } from 'src/pages/cropwise-ai/cropwise-ai-chat.context';
import { AIGradientColors } from 'src/theme/colors';

import { CropwiseAiTabsEnum } from '../cropwise-ai.enum';
import { ConversationEmpty } from './conversation-empty/conversation-empty.component';
import { AnswerAvatar } from './conversation-item/conversation-item.style';
import { useCropwiseAiConversation } from './cropwise-ai-conversation.hook';
import * as Styled from './cropwise-ai-conversation.style';

interface CropwiseAiConversationProps {
  showType: CropwiseAiTabsEnum;
  sessionId: string | null;
}

const CropwiseAiConversation = ({
  showType,
  sessionId,
}: CropwiseAiConversationProps) => {
  const { t } = useTranslation();
  const { messages, sendMessage, isLoading } = useChatContext();
  const { historicalMessages, isFetching } =
    useCropwiseAiConversation(sessionId);
  const containerRef = useRef<HTMLDivElement>(null);

  const markdownComponents = useMemo(
    () => ({
      table: (props: { children?: ReactNode }) => (
        <Styled.TableWrapper>
          <table>{props.children}</table>
        </Styled.TableWrapper>
      ),
    }),
    [],
  );

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, historicalMessages]);

  if (showType === CropwiseAiTabsEnum.HIDE) return null;

  if (isFetching) {
    return (
      <Styled.Container>
        <Skeleton active />
      </Styled.Container>
    );
  }

  const hasAnyMessages = historicalMessages.length > 0 || messages.length > 0;

  if (!hasAnyMessages) {
    return (
      <Styled.Container>
        <ConversationEmpty
          onPromptSelect={(prompt) => void sendMessage(prompt)}
        />
      </Styled.Container>
    );
  }

  const lastMsgId = messages.at(-1)?.id;

  const renderAudioBubble = (content: string) => (
    <Styled.AudioBubbleContent>
      <Styled.AudioBadge>
        <MicNoneIcon />
        {t('cropwise_ai.conversation.voice_message')}
      </Styled.AudioBadge>
      <span>{content}</span>
    </Styled.AudioBubbleContent>
  );

  const historicalItems = historicalMessages.map((msg, i) => {
    const role = msg.role === 'human' ? 'user' : 'ai';
    const isAudio = role === 'user' && msg.type === 'audio';

    return {
      key: `h-${i}`,
      role,
      content: msg.content,
      ...(isAudio && { contentRender: renderAudioBubble }),
    };
  });

  const liveItems = messages.map((msg) => {
    const role = msg.role === 'assistant' ? 'ai' : msg.role;
    const isStreamingThis = isLoading && msg.id === lastMsgId;

    return {
      key: msg.id,
      role,
      content: msg.content,
      ...(role === 'user' && {
        contentRender: (content: string) => {
          if (msg.isTranscribing) {
            return <span>{t('cropwise_ai.conversation.transcribing')}</span>;
          }
          if (msg.isAudio) {
            return renderAudioBubble(content);
          }
          return <span>{content}</span>;
        },
      }),
      ...(role === 'ai' && {
        loading: isStreamingThis && !msg.content,
        avatar:
          isStreamingThis && !msg.content ? (
            <AnswerAvatar icon={<BalanceAIColoredIcon />} />
          ) : undefined,
        contentRender: (content: string) => (
          <XMarkdown
            content={content}
            openLinksInNewTab
            components={markdownComponents}
            streaming={{
              hasNextChunk: isStreamingThis,
              tail: isStreamingThis,
              enableAnimation: true,
            }}
          />
        ),
      }),
    };
  });

  return (
    <Styled.Container ref={containerRef}>
      <Bubble.List
        role={{
          user: {
            placement: 'end',
            shape: 'corner',
            styles: {
              body: {
                borderRadius: '24px 4px 24px 24px',
                background: AIGradientColors[10],
              },
            },
          },
          ai: {
            variant: 'borderless',
            contentRender: (content) => (
              <XMarkdown
                content={content}
                openLinksInNewTab
                components={markdownComponents}
              />
            ),
          },
        }}
        items={[...historicalItems, ...liveItems]}
      />
    </Styled.Container>
  );
};

export { CropwiseAiConversation };
